import {
  PaymentMethod,
  PaymentType,
  Task,
  retryPayment as RETRY_PAYMENT,
  retryPayoutTransfer as RETRY_PAYOUT_TRANSFER,
  Payment,
  PaymentMethodStatus,
  PaymentMethodType,
  getTask,
  PaymentFrequency,
  updateTask as UPDATE_TASK,
  TaskType,
  CSGetSub as GET_SUB,
  AccountDirection,
  PaymentStatus,
  SubscriptionTier,
} from '@admiin-com/ds-graphql';
import React from 'react';
import { useSelectedEntity } from '../../hooks/useSelectedEntity/useSelectedEntity';
import {
  createPayment as CREATE_PAYMENT,
  createTaskPayment as CREATE_TASK_PAYMENT,
} from '@admiin-com/ds-graphql';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  backendDateFromUnixSeconds,
  maskCreditCardNumberSimple,
} from '@admiin-com/ds-common';
import { useSnackbar } from '@admiin-com/ds-web';

import { createPaymentPayId as CREATE_PAYMENT_PAYID } from '@admiin-com/ds-graphql';
import {
  calculateFee,
  isDeclinedTask,
  isPayableTask,
  isPayoutFailedTask,
  isReocurringTask,
  isSignableTask,
  isTaskScheduled,
} from '../../helpers/tasks';
import VerificationDlg from '../../pages/VerificationDlg/VerificationDlg';
import AddPaymentMethodModal from '../AddPaymentMethodModal/AddPaymentMethodModal';
import AddSignatureModal from '../AddSignatureModal/AddSignatureModal';
import ReocurringConfirmModal from '../ReocurringConfirmModal/ReocurringConfirmModal';
import { isVerifiedEntity } from '../../helpers/entities';
import { useSearchParams } from 'react-router-dom';
import { PaymentSubmittedModalContainer } from '../PaymentSubmittedModal';
import { t } from 'i18next';
import { useCurrentUser } from '../../hooks/useCurrentUser/useCurrentUser';
import { TaskPayoutDetailModal } from '../../pages/TaskDetail/TaskPayoutDetailModal';
import { useFeatureFlags } from '../../hooks/useFeatureFlags/useFeatureFlags';
import {
  useEntity,
  useEntityId,
} from '../../features/hooks/useEntityContext/useEntityContext';
import useTaskSearchFilters from '../../features/hooks/useTaskSearchFilters/useTaskSearchFilters';
import useTasksFromurlByEntityId from '../../features/hooks/useTasksFromUrl/useSearchTasks';

export interface PaymentContainerProps {
  children: React.ReactNode;
  tasks: Task[];
  onSuccess: () => void;
}

export type PaymentDetailData = {
  task: Task;
  scheduledAt?: Date;
  id: string;
  installments?: number;
  entityId?: string;
  type?: PaymentType;
  paymentFrequency?: PaymentFrequency;
  isUnlimitedRecurring?: boolean;
};

const PaymentContext = React.createContext<any>(null);

export type PaymentAPIStatus =
  | 'INITIAL'
  | 'PENDING'
  | 'SIGNED'
  | 'PAYING'
  | 'PAID';

export function PaymentContainer({
  children,
  tasks,
  onSuccess,
}: PaymentContainerProps) {
  const [paymentDetails, setPaymentDetails] = React.useState<
    Array<PaymentDetailData>
  >([]);
  const [paymentMethod, setPaymentMethod] = React.useState<
    PaymentMethod | null | undefined | string
  >(null);
  const [bankPaymentMethod, setBankPaymentMethod] = React.useState<
    PaymentMethod | null | undefined
  >(null);

  const [paymentAPIStatus, setPaymentAPIStatus] =
    React.useState<PaymentAPIStatus>('INITIAL');
  const entityId = useEntityId();
  const { entity } = useEntity();
  const { filters } = useTaskSearchFilters();
  const { refetch } = useTasksFromurlByEntityId({
    entityId,
    ...filters,
    disableAutoFetch: true,
  });

  const paymentMethods = React.useMemo(
    () =>
      entity?.paymentMethods?.items
        ?.filter(
          (method: PaymentMethod | null) =>
            method?.status === PaymentMethodStatus.ACTIVE &&
            (method?.paymentMethodType === PaymentMethodType.CARD ||
              method?.paymentMethodType === PaymentMethodType.GOOGLEPAY ||
              method?.paymentMethodType === PaymentMethodType.APPLEPAY ||
              (method?.paymentMethodType === PaymentMethodType.BANK &&
                method.accountDirection !== AccountDirection.DISBURSEMENT) ||
              method?.paymentMethodType === PaymentMethodType.PAYID ||
              method?.paymentMethodType === PaymentMethodType.PAYTO)
        )
        ?.map((method: PaymentMethod | null) => ({
          ...method,
          value: method,
          label:
            maskCreditCardNumberSimple(
              method?.number ?? method?.accountNumber ?? ''
            ) ?? '',
        })) ?? [],
    [entity]
  );

  const [paymentMethodModal, setPaymentMethodModal] = React.useState<
    'CC' | 'PAY_TO_VERIFY' | 'PAY_ID' | 'BankAccount' | 'ConnectedAccount'
  >('CC');

  const [createPayment] = useMutation(gql(CREATE_PAYMENT));
  const [retryPayment] = useMutation(gql(RETRY_PAYMENT));
  const [retryPayoutTransfer] = useMutation(gql(RETRY_PAYOUT_TRANSFER));
  const [createPaymentPayId] = useMutation(gql(CREATE_PAYMENT_PAYID));

  const [paymentSubmittedModal, setPaymentSubmittedModal] = React.useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });

  const paymentMethodId =
    typeof paymentMethod === 'string'
      ? bankPaymentMethod?.id
      : paymentMethod?.id;

  const updatePayment = React.useCallback(
    async (paymentData: PaymentDetailData) => {
      setPaymentDetails((payments) =>
        payments.map((payment) =>
          payment.task.id === paymentData?.task?.id
            ? { ...payment, ...paymentData }
            : payment
        )
      );
      if (
        !paymentDetails.find(
          (payment) => payment.task.id === paymentData?.task?.id
        )
      ) {
        setPaymentDetails(paymentDetails.concat(paymentData));
      }
    },
    [paymentDetails, setPaymentDetails]
  );

  React.useEffect(() => {
    if (paymentMethods && paymentMethods.length > 0) {
      const paymentMethod =
        paymentMethods?.find(
          (item: any) => item?.id === entity?.paymentMethodId
        ) ?? paymentMethods[0];
      if (paymentMethod) setPaymentMethod(paymentMethod as any);
    }
  }, [entity?.paymentMethodId, paymentMethods]);

  const getBillPayments = (tasks: Task[]) => {
    return tasks
      .map(
        (task: Task) =>
          ({
            id: task.id,
            type: PaymentType.PAY_NOW,
            scheduledAt: new Date(),
            entityId: task.entityId,
            paymentFrequency: task.paymentFrequency,
            installments: task?.numberOfPayments ?? 1,
            isUnlimitedRecurring: task?.isUnlimitedRecurring ?? false,
            ...paymentDetails.find(
              (paymentDetail) => paymentDetail.task.id === task?.id
            ),
          } as PaymentDetailData)
      )
      .map((payment: PaymentDetailData) => {
        return {
          id: payment.id,
          entityId: payment.entityId,
          installments: payment.installments,
          paymentType: payment.type,
          paymentFrequency: payment.paymentFrequency,
          isUnlimitedRecurring: payment.isUnlimitedRecurring,
          scheduledAt: backendDateFromUnixSeconds(
            (payment?.scheduledAt ?? new Date()).getTime() / 1000
          ),
        };
      });
  };

  React.useEffect(() => {
    if (paymentAPIStatus === 'PAID') {
      setTimeout(() => {
        setPaymentAPIStatus('INITIAL');
      }, 2000);
    }
  }, [paymentAPIStatus]);

  const [searchParams, setSearchParams] = useSearchParams();

  const paymentSubmit = async (tasks: Task[], paymentMethodId: string) => {
    tasks = tasks.filter((task) => task.type !== TaskType.SIGN_ONLY);
    if (tasks.length === 0) return;

    setPaymentAPIStatus('PAYING');
    if (entity) {
      try {
        const billPayments = getBillPayments(tasks);
        await createPayment({
          variables: {
            input: {
              entityId: entity.id,
              paymentMethodId: paymentMethodId,
              billPayments,
            },
          },
          // refetchQueries: [
          //   // {
          //   //   query: gql(searchTasks),
          //   //   variables: { entityId: entity.id, status: 'INCOMPLETE' }, //TODO: add type if refetch not working
          //   // },
          //   // {
          //   //   query: gql(searchTasks),
          //   //   variables: { entityId: entity.id, status: 'COMPLETED' }, //TODO: add type if refetch not working
          //   // },
          //   ...tasks.map((task) => ({
          //     query: gql(getTask),
          //     variables: {
          //       id: task.id,
          //       entityId: entity.id,
          //     },
          //   })),
          // ],
          awaitRefetchQueries: true,
        });

        await refetch();
        setPaymentAPIStatus('PAID');
      } catch (error: any) {
        showSnackbar({
          message: t(error?.message ?? 'Create payment failed', {
            ns: 'errors',
          }),
          severity: 'error',
          vertical: 'bottom',
        });

        setPaymentAPIStatus('INITIAL');
        throw error;
      }
    }
  };

  const [createTaskPayment] = useMutation(gql(CREATE_TASK_PAYMENT));

  const retryFailedOrDeclinedPayment = async (payment: Payment) => {
    if (payment && payment.status === PaymentStatus.DECLINED)
      if (paymentMethod && typeof paymentMethod !== 'string') {
        await retryPayment({
          variables: {
            input: { id: payment.id, paymentMethodId: paymentMethodId },
          },
          // refetchQueries: [
          // {
          //   query: gql(tasksByEntityTo),
          //   variables: {
          //     entityId: entityId,
          //     status: 'INCOMPLETE',
          //   },
          // },
          // ],
        });
        await refetch();
      }
    if (payment && payment.status === PaymentStatus.PAYOUT_FAILED) {
      await retryPayoutTransfer({
        variables: {
          input: { id: payment.id },
        },
        // refetchQueries: [
        //   {
        //     query: gql(tasksByEntityTo),
        //     variables: {
        //       entityId: entityId,
        //       status: 'INCOMPLETE',
        //     },
        //   },
        // ],
      });

      await refetch();
    }
  };

  const retryFailedPayment = async (payment: Payment) => {
    setPaymentAPIStatus('PAYING');
    try {
      await retryFailedOrDeclinedPayment(payment);
      setPaymentAPIStatus('INITIAL');
    } catch (error: any) {
      showSnackbar({
        message: t(error?.message ?? 'Retry payment failed', { ns: 'errors' }),
        severity: 'error',
        vertical: 'bottom',
      });
      setPaymentAPIStatus('INITIAL');
    }
  };
  const retryAllDeclinedPayments = async (task: Task) => {
    setPaymentAPIStatus('PAYING');

    try {
      for (const payment of task.payments?.items ?? []) {
        if (payment) await retryFailedOrDeclinedPayment(payment);
      }
      if (isPayoutFailedTask(task)) {
        await updateTask({
          variables: {
            input: {
              id: task.id,
              entityId: task.entityId,
              payoutErrors: null,
            },
          },
        });
      }
      setPaymentAPIStatus('PAID');
    } catch (error: any) {
      showSnackbar({
        message: t(error?.message ?? 'Retry payment failed', { ns: 'errors' }),
        severity: 'error',
        vertical: 'bottom',
      });
      setPaymentAPIStatus('INITIAL');
      throw error;
    }
  };
  const taskPaymentSubmit = async (task: Task) => {
    setPaymentAPIStatus('PAYING');
    try {
      await createTaskPayment({
        variables: {
          input: {
            taskId: task?.id,
            entityId: entity?.id,
            paymentMethodId: paymentMethodId,
          },
        },
        refetchQueries: [
          // {
          //   query: gql(tasksByEntityTo),
          //   variables: {
          //     entityId: entity?.id,
          //     status: 'INCOMPLETE',
          //   },
          // },
          // {
          //   query: gql(tasksByEntityTo),
          //   variables: {
          //     entityId: entity?.id,
          //     status: 'COMPLETED',
          //   },
          // },
          {
            query: gql(getTask),
            variables: {
              id: task?.id,
              entityId: entity?.id,
            },
          },
        ],
        awaitRefetchQueries: true,
      });
      await refetch();
      setPaymentAPIStatus('PAID');
    } catch (error: any) {
      showSnackbar({
        message: t(error?.message ?? 'Create payment failed', { ns: 'errors' }),
        severity: 'error',
        vertical: 'bottom',
      });
      setPaymentAPIStatus('INITIAL');
      throw error;
    }
  };

  const createPayId = async (tasks: Task[]) => {
    setPaymentAPIStatus('SIGNED');

    try {
      await createPaymentPayId({
        variables: {
          input: {
            entityId,
            billPayments: getBillPayments(tasks),
          },
        },
        refetchQueries: [
          {
            query: gql(getTask),
            variables: {
              id: tasks[0]?.id,
              entityId: entityId,
            },
          },
        ],
      });

      await refetch();
      setPaymentAPIStatus('PAID');
    } catch (error: any) {
      showSnackbar({
        message: t(error?.message ?? 'Create payment failed', { ns: 'errors' }),
        severity: 'error',
        vertical: 'bottom',
      });
      setPaymentAPIStatus('INITIAL');
      throw error;
    }
  };

  const createPayTo = async (agreements: any[], tasks: Task[]) => {
    setPaymentAPIStatus('PAYING');
  };

  const [updateTask] = useMutation(gql(UPDATE_TASK));
  const user = useCurrentUser();
  const selectedSignatureKey = user?.selectedSignatureKey;

  const { data: subData } = useQuery(gql(GET_SUB));
  const userId = subData?.sub;

  const [showAddSignModal, setShowAddSignModal] =
    React.useState<boolean>(false);
  const [verificationModal, setVerificationModal] =
    React.useState<boolean>(false);
  const [addPaymentMethodModal, setAddPaymentMethodModal] =
    React.useState<boolean>(false);
  const [reocurringConfirmModal, setReocurringConfirmModal] =
    React.useState<boolean>(false);
  const [reOcurringTasks, setReOcurringTasks] = React.useState<Task[]>([]);
  const [billsToSubmit, setBillsToSubmit] = React.useState<Task[]>([]);

  const { featureFlags } = useFeatureFlags();
  const isNewLayout = featureFlags?.newLayout;

  const showPaymentModal = (
    bills: Task[],
    paymentMethod: string | PaymentMethod | null | undefined
  ) => {
    const isScheduling = bills.every(
      (task) =>
        isPayableTask(task) &&
        paymentDetails.find(
          (detail: PaymentDetailData) => detail.task.id === task?.id
        )?.type === PaymentType.SCHEDULED
    );
    if (!isNewLayout) {
      if (isScheduling) {
        setSearchParams({ status: 'Scheduled' });
      } else {
        setSearchParams({ status: 'Completed' });
      }
    }

    // setTimeout(() => {
    //   refreshSelectedTasks();
    // }, 3000);

    const notPayable = bills.every((task: Task) => !isPayableTask(task));
    const notSignable = bills.every((task: Task) => !isSignableTask(task), {
      userId,
    });

    const title = isScheduling ? 'Scheduled' : notPayable ? 'Signed' : 'Paid';

    if (paymentMethod === 'connectedAccount') {
      setPaymentSubmittedModal({
        open: true,
        title: t('payIDTransferTitle', { ns: 'payment' }),
        description: t('payIDTransferSubTitle', { ns: 'payment' }),
      });
    } else
      setPaymentSubmittedModal({
        open: true,
        title: `Tasks ${title}`,
        description: `Your tasks have been ${title?.toLowerCase()} successfully`,
      });
  };

  const submitBills = async (bills: Task[], check = true) => {
    try {
      if (check && !canSubmitBill(bills)) return;
      if (bills.some((task) => isPayableTask(task))) await payDocument(bills);
      showPaymentModal(bills, paymentMethod);
    } catch (error) {
      console.error(error);
      throw error;
    }
    // setMultiShow(false);
  };

  const payBillsWithPaymentMethod = async (
    payableTasks: Task[],
    paymentMethod: PaymentMethod
  ) => {
    payableTasks = await payScheduledTasks(payableTasks);
    if (
      paymentMethod &&
      typeof paymentMethod !== 'string' &&
      'id' in paymentMethod
    )
      await paymentSubmit(payableTasks, paymentMethod?.id);
    showPaymentModal(payableTasks, paymentMethod);
  };
  const handleAddPaymentMethod = (paymentMethod?: any) => {
    if (paymentMethod) {
      if (
        paymentMethodModal === 'BankAccount' ||
        paymentMethodModal === 'ConnectedAccount'
      ) {
        setBankPaymentMethod(paymentMethod);
        setPaymentMethod(paymentMethod?.id);
      } else {
        console.log(
          'cc payment method finished',
          paymentMethod,
          paymentMethodModal
        );
        // setPaymentMethod({ value: paymentMethod, ...paymentMethod, });
      }

      payBillsWithPaymentMethod(billsToSubmit, paymentMethod);
      // setTimeout(() => submitBills(billsToSubmit, false), 5000);
    }
  };
  const [payoutModalOpen, setPayotModalOpen] = React.useState<boolean>(false);
  const [payoutModalTask, setPayoutModalTask] = React.useState<Task | null>(
    null
  );

  const canSubmitBill = (tasks: Task[]): boolean => {
    if (!selectedSignatureKey && tasks.some((task) => isSignableTask(task))) {
      setShowAddSignModal(true);
      return false;
    }
    if (tasks.some((task) => task.payoutErrors)) {
      setBillsToSubmit(tasks);
      setPayotModalOpen(true);
      setPayoutModalTask(tasks.find((task) => task.payoutErrors) || null);
      return false;
    }

    if (tasks.some((task) => isPayableTask(task))) {
      if (entity && !isVerifiedEntity(entity)) {
        setVerificationModal(true);
        return false;
      }

      if (!paymentMethod) {
        setBillsToSubmit(tasks);
        setPaymentMethodModal('CC');
        setAddPaymentMethodModal(true);
        return false;
      } else if (paymentMethod === 'connectedAccount') {
        // setPaymentMethodModal('ConnectedAccount');
        // setAddPaymentMethodModal(true);
      } else if (typeof paymentMethod === 'string') {
        if (!bankPaymentMethod) {
          setPaymentMethodModal('BankAccount');
          setAddPaymentMethodModal(true);
          return false;
        }
      } else {
        const reOcurringTasks = tasks.filter((task) => isReocurringTask(task));
        if (reOcurringTasks.length > 0) {
          setReOcurringTasks(reOcurringTasks);
          setBillsToSubmit(tasks);
          setReocurringConfirmModal(true);
          return false;
        }
      }
    }
    return true;
  };
  const payScheduledTasks = async (payableTasks: Task[]) => {
    const newPayableTasks = [];
    for (const payableTask of payableTasks) {
      if (
        payableTask &&
        paymentDetails.find(
          (detail: PaymentDetailData) =>
            detail.type === 'PAY_NOW' && detail.task.id === payableTask.id
        ) &&
        isTaskScheduled(payableTask)
      ) {
        if (!isDeclinedTask(payableTask) && !isPayoutFailedTask(payableTask)) {
          await taskPaymentSubmit(payableTask);
        } else newPayableTasks.push(payableTask);
      }
      if (payableTask) {
        if (isDeclinedTask(payableTask) || isPayoutFailedTask(payableTask)) {
          await retryAllDeclinedPayments(payableTask);
        } else newPayableTasks.push(payableTask);
      }
    }
    return newPayableTasks;
  };

  const payDocument = async (payableTasks: Task[]) => {
    payableTasks = await payScheduledTasks(payableTasks);
    if (paymentMethod === 'connectedAccount') {
      await createPayId(payableTasks);
    } else if (
      paymentMethod &&
      typeof paymentMethod !== 'string' &&
      'id' in paymentMethod
    )
      await paymentSubmit(payableTasks, paymentMethod?.id);
  };

  const showSnackbar = useSnackbar();

  const onSubmit = (canSubmit?: boolean) => {
    submitBills(tasks, canSubmit);
  };

  return (
    <PaymentContext.Provider
      value={{
        paymentDetails,
        taskPaymentSubmit,
        paymentMethod,
        paymentMethods,
        createPayTo,
        createPayId,
        setPaymentMethod,
        retryPayment: retryFailedPayment,
        submitBills,
        bankPaymentMethod,
        setBankPaymentMethod,
        setPaymentAPIStatus,
        paymentAPIStatus,
        updatePayment,
        getBillPayments,
        setPaymentDetails,
        loading: paymentAPIStatus !== 'INITIAL',
        paymentMethodId,
        onSubmit,
      }}
    >
      {children}
      {entity ? (
        <VerificationDlg
          entity={entity}
          onSuccess={() => {
            console.log('success');
            // setVerified(true);
          }}
          open={verificationModal}
          onClose={() => setVerificationModal(false)}
        />
      ) : null}
      <AddPaymentMethodModal
        open={addPaymentMethodModal}
        type={paymentMethodModal}
        handleClose={() => setAddPaymentMethodModal(false)}
        onSuccess={handleAddPaymentMethod}
      />

      <AddSignatureModal
        open={showAddSignModal}
        handleClose={() => setShowAddSignModal(false)}
        // handleSave={signatureAddHandler}
      />

      <ReocurringConfirmModal
        reOcurringTasks={reOcurringTasks}
        onConfirm={() => {
          submitBills(billsToSubmit, false);
        }}
        open={reocurringConfirmModal}
        onClose={() => setReocurringConfirmModal(false)}
      />
      <PaymentSubmittedModalContainer
        // open={true}
        open={paymentSubmittedModal.open}
        onClose={() => {
          setPaymentSubmittedModal({ open: false, title: '', description: '' });
          onSuccess();
        }}
        title={paymentSubmittedModal.title}
        description={paymentSubmittedModal.description}
      />
      <TaskPayoutDetailModal
        task={payoutModalTask}
        isRetry={true}
        open={payoutModalOpen}
        onClose={() => setPayotModalOpen(false)}
        onSubmit={() => {
          setPayotModalOpen(false);
          submitBills(billsToSubmit, false);
        }}
      />
    </PaymentContext.Provider>
  );
}

export const usePaymentContext = () => {
  const context = React.useContext(PaymentContext);
  const { entity } = useSelectedEntity();

  const getTotal = React.useCallback((tasks: Task[]) => {
    return tasks?.reduce(
      (total, task) => {
        return {
          totalAmount: total.totalAmount + (task?.amount ?? 0),
        };
      },
      { totalAmount: 0 }
    );
  }, []);

  const getFees = React.useCallback(
    (tasks: Task[], paymentMethodType?: PaymentMethodType) => {
      return calculateFee(
        tasks,
        paymentMethodType ??
          (!context?.paymentMethod ||
          (typeof context?.paymentMethod === 'string' &&
            context?.paymentMethod !== 'connectedAccount')
            ? PaymentMethodType.CARD
            : context?.paymentMethod === 'connectedAccount'
            ? PaymentMethodType.BANK
            : context?.paymentMethod?.paymentMethodType),
        context?.paymentDetails ?? [],
        entity?.subscriptionTier || SubscriptionTier.PRO
      );
    },
    [context?.paymentMethod, context?.paymentDetails]
  );

  return {
    getFees,
    getTotal,
    ...(context ?? {}),
    hasTaxNumber: !!entity?.taxNumber,
  };
};

export const usePaymentContextDetail = (task: Task | null) => {
  const context = React.useContext(PaymentContext);
  if (!context) {
    throw new Error(
      'usePaymentContextDetail must be used within a PaymentContainer'
    );
  }

  const paymentDetail = React.useMemo(
    () =>
      context.paymentDetails?.find(
        (payment: PaymentDetailData) => payment?.task?.id === task?.id
      ),
    [context.paymentDetails, task?.id]
  );

  return { ...context, paymentDetail };
};

export default PaymentContainer;
